import { useEffect, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Lock, Users, Mail, Instagram } from "lucide-react";
import { LangProvider, useLang, CONTACT_EMAIL } from "./i18n";

const ANCHORS = ["about", "servizi", "metodo", "casi", "contatti"];

const LangToggle = ({ className = "" }) => {
  const { lang, setLang } = useLang();
  return (
    <div className={`inline-flex rounded-full border border-[#0A1128]/15 bg-white/60 p-0.5 text-xs font-bold ${className}`} data-testid="site-lang-toggle">
      {["it", "en"].map((l) => (
        <button key={l} onClick={() => setLang(l)} aria-pressed={lang === l} data-testid={`site-lang-${l}`}
          className={`h-8 min-w-[40px] rounded-full px-2 uppercase transition-colors ${lang === l ? "bg-[#0A1128] text-white" : "text-[#0A1128] hover:bg-white"}`}>{l}</button>
      ))}
    </div>
  );
};

const Header = () => {
  const { t } = useLang();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 12);
    on(); window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);
  useEffect(() => setOpen(false), [pathname]);
  const go = (id) => {
    setOpen(false);
    if (pathname !== "/") { navigate(`/#${id}`); return; }
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };
  return (
    <header className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow] duration-300 pt-[env(safe-area-inset-top)] ${scrolled || open ? "bg-[#7CC6FA]/90 backdrop-blur-xl shadow-[0_8px_30px_rgba(10,17,40,0.08)]" : "bg-transparent"}`} data-testid="site-header">
      <div className="max-w-[1320px] mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between gap-4">
        <Link to="/" className="shrink-0" data-testid="site-logo" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}><img src="/brand/logo-core.png" alt="core. – the heart of your business" className="h-8 sm:h-9 w-auto" /></Link>
        <nav className="hidden lg:flex items-center gap-1" data-testid="site-nav">
          {ANCHORS.map((a) => (
            <button key={a} onClick={() => go(a)} className="px-3 py-2 rounded-full text-sm font-semibold text-[#0A1128] hover:bg-white/50 transition-colors" data-testid={`site-nav-${a}`}>{t.nav[a]}</button>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <LangToggle className="hidden sm:inline-flex" />
          <Link to="/area-clienti" className="hidden md:inline-flex items-center gap-1.5 h-10 px-4 rounded-full border border-[#0A1128]/20 text-sm font-bold text-[#0A1128] hover:bg-white/60 transition-colors" data-testid="site-area-clienti"><Users className="h-4 w-4" />{t.nav.clienti}</Link>
          <Link to="/login" className="hidden md:inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-[#0A1128] text-sm font-bold text-white hover:bg-[#E8173A] transition-colors" data-testid="site-area-riservata"><Lock className="h-4 w-4" />{t.nav.riservata}</Link>
          <button onClick={() => setOpen((o) => !o)} className="lg:hidden h-11 w-11 rounded-full bg-white/70 flex items-center justify-center text-[#0A1128]" aria-label={t.nav.menu} data-testid="site-menu-button">{open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}</button>
        </div>
      </div>
      {open && (
        <div className="lg:hidden border-t border-[#0A1128]/10 px-5 pb-6 pt-3 max-h-[calc(100dvh-72px)] overflow-y-auto" data-testid="site-mobile-menu">
          {ANCHORS.map((a) => (
            <button key={a} onClick={() => go(a)} className="block w-full text-left py-3.5 font-display text-2xl font-extrabold text-[#0A1128] border-b border-[#0A1128]/10" data-testid={`site-mnav-${a}`}>{t.nav[a]}</button>
          ))}
          <div className="grid grid-cols-2 gap-2 mt-5">
            <Link to="/area-clienti" className="h-12 rounded-full border border-[#0A1128]/20 flex items-center justify-center gap-1.5 font-bold text-[#0A1128]" data-testid="site-m-area-clienti"><Users className="h-4 w-4" />{t.nav.clienti}</Link>
            <Link to="/login" className="h-12 rounded-full bg-[#0A1128] text-white flex items-center justify-center gap-1.5 font-bold" data-testid="site-m-area-riservata"><Lock className="h-4 w-4" />{t.nav.riservata}</Link>
          </div>
          <LangToggle className="mt-4" />
        </div>
      )}
    </header>
  );
};

const Footer = () => {
  const { t } = useLang();
  return (
    <footer className="bg-[#0A1128] text-white pb-[env(safe-area-inset-bottom)]" data-testid="site-footer">
      <div className="max-w-[1320px] mx-auto px-5 sm:px-8 py-14 grid gap-10 md:grid-cols-[1.4fr_1fr_1fr]">
        <div>
          <img src="/brand/logo-core-white.png" alt="core." className="h-9 w-auto" />
          <p className="mt-4 text-sm text-white/60 max-w-sm">{t.seo.title.replace("Core – ", "")}</p>
        </div>
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF5A72] mb-3">{t.footer.contatti}</div>
          <a href={`mailto:${CONTACT_EMAIL}`} className="inline-flex items-center gap-2 text-sm font-semibold hover:text-[#7CC6FA] transition-colors" data-testid="site-footer-email"><Mail className="h-4 w-4" />{CONTACT_EMAIL}</a>
        </div>
        <div className="flex flex-col gap-2 text-sm">
          <Link to="/privacy" className="hover:text-[#7CC6FA] transition-colors" data-testid="site-footer-privacy">{t.footer.privacy}</Link>
          <Link to="/area-clienti" className="hover:text-[#7CC6FA] transition-colors">{t.nav.clienti}</Link>
          <Link to="/login" className="hover:text-[#7CC6FA] transition-colors">{t.nav.riservata}</Link>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-[1320px] mx-auto px-5 sm:px-8 py-5 flex flex-wrap items-center justify-between gap-3 text-xs text-white/50">
          <span>© {new Date().getFullYear()} core. – the heart of your business. {t.footer.rights}</span>
          <span className="inline-flex items-center gap-1"><Instagram className="h-3.5 w-3.5" />corefvg.com</span>
        </div>
      </div>
    </footer>
  );
};

export const SiteLayout = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) setTimeout(() => document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: "smooth" }), 80);
    else window.scrollTo(0, 0);
  }, [pathname, hash]);
  return (
    <LangProvider>
      <div className="site-root min-h-screen bg-[#EAF6FF] text-[#0A1128] overflow-x-hidden" data-testid="public-site">
        <Header />
        <main><Outlet /></main>
        <Footer />
      </div>
    </LangProvider>
  );
};
